import { useEffect, useState } from 'react';
import { Card, Checkbox, Col, Drawer, Empty, Form, Input, message, Popover, Row, Select, Slider, Space, Spin, Tag, Typography } from 'antd';
import {
  DownOutlined,
  EditOutlined,
  FileTextOutlined,
  FilterOutlined,
  LikeOutlined,
  RobotOutlined,
  SearchOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { getFormGroups, searchCandidates } from '../../../../services/dropdownApi';
import { updateJobDescription } from '../../../../services/jobsApi';
import { migrateDiceCandidate } from '../../../../services/candidatesApi';
import { resolveDiceProfileId } from '../../../../services/diceCandidateMapper';
import { tagCandidatesToJob } from '../../../../services/popupApi';
import { getShortlistedStageValue } from '../../../../services/workflowApi';
import AppButton from '../../../../components/AppButton';
import DescriptionBlock from '../../../../components/detail/DescriptionBlock';
import ParamListView from '../../../../components/ParamListView';
import ParamCardView from '../../../../components/ParamCardView';
const { Text } = Typography;

// Tag to Jobs and Quick Submit still route to their existing popups; Shortlist
// bypasses the modal entirely and calls the same Tag to Jobs API directly with
// the "shortlisted" stage value resolved from the DB (see handleShortlist) —
// never a hardcoded string.
const getSourcingActions = ({ onShortlist }) => [
  { key: 'quick-submit', name: 'Quick Submit', label: 'Quick Submit', route: '/popup/quick-submit', maxSelect: 1 },
  { key: 'shortlist',    name: 'Shortlist',    label: 'Shortlist',    onSelect: onShortlist },
  { key: 'mail',         name: 'Send Email',   label: 'Send Email',   route: '/popup/mail' },
  { key: 'tag-to-job',   name: 'Tag to Jobs',  label: 'Tag to Jobs',  route: '/popup/tag-to-job' },
  { key: 'add-notes',    name: 'Add Notes',    label: 'Add Notes',    route: '/popup/add-notes',    maxSelect: 1 },
];

const DATE_OPTIONS = [
  { label: 'Today',        value: 'today' },
  { label: 'Yesterday',    value: 'yesterday' },
  { label: 'Last 7 Days',  value: '7days' },
  { label: 'Last 30 Days', value: '30days' },
  { label: 'This Month',   value: 'months' },
  { label: 'Last Month',   value: 'lastmonth' },
];

const SOURCE_OPTS = ['All', 'Internal', 'Dice', 'Network'];
const EXP_MARKS   = { 0: '0', 15: '15', 30: '30+' };

const getCandidateFields = ({ onMigrate, migratingDiceId, migratedDiceIds }) => [
  {
    label: 'Name',
    value: 'name',
    width: 190,
    cardRole: 'name',
    render: (value) => <Text className="job-cell-primary sourcing-candidate-name">{value || '-'}</Text>,
  },
  { label: 'Designation', value: 'designation', width: 200, cardRole: 'subtitle' },
  { label: 'Location/Exp', value: 'locationExp', width: 160 },
  { label: 'Location', value: 'location', cardRole: 'location', tableHidden: true },
  { label: 'Exp', value: 'exp', cardRole: 'exp', tableHidden: true },
  {
    label: 'Skills',
    value: 'skills',
    width: 220,
    cardRole: 'skills',
    render: (skills) => (
      <Space size={[4, 4]} wrap>
        {(Array.isArray(skills) ? skills : []).slice(0, 3).map((skill) => (
          <Tag key={skill} className="sourcing-skill-tag">{skill}</Tag>
        ))}
        {Array.isArray(skills) && skills.length > 3 && (
          <Popover
            trigger="click"
            placement="bottomLeft"
            overlayClassName="sourcing-skills-popover"
            content={
              <div className="sourcing-skills-popup">
                {skills.slice(3).map((skill) => (
                  <Tag key={skill} className="sourcing-skill-tag">{skill}</Tag>
                ))}
              </div>
            }
          >
            <span className="sourcing-more-skills">+{skills.length - 3}</span>
          </Popover>
        )}
        {(!Array.isArray(skills) || skills.length === 0) && '-'}
      </Space>
    ),
  },
  { label: 'Created on', value: 'createdOn', width: 150, cardRole: 'date' },
  {
    label: 'Action',
    value: 'action',
    width: 120,
    sorter: false,
    cardRole: 'action',
    render: (_value, record) => {
      const sourceFields = [
        record?.sourceType,
        record?.profileSource,
        record?.selectedSource,
        record?.customFields?.sourceType,
        record?.customFields?.profileSource,
      ].flat();
      const isDice = sourceFields.some(
        (value) => String(value ?? '').trim().toLowerCase() === 'dice',
      ) || Boolean(record?.customFields?.diceProfileData);
      if (!isDice) {
        return <AppButton size="small" variant="soft" icon={<LikeOutlined />} className="sourcing-shortlist-btn">Shortlist</AppButton>;
      }

      const diceId = resolveDiceProfileId(record);
      const noDiceId = !diceId;
      const isCurrentRowMigrating = String(migratingDiceId) === String(diceId);
      const isMigrated = migratedDiceIds.has(String(diceId));
      const disabled = noDiceId || isMigrated;

      if (process.env.NODE_ENV === 'development') {
        console.log('[DiceMigration] Candidate before render:', {
          id: record?.id,
          diceId,
          candidateId: record?.candidateId,
          nestedDiceProfileData: record?.customFields?.diceProfileData,
        });
        if (!diceId) {
          console.warn('[DiceMigration] Migrate disabled: no Dice profile id found on row', record);
        }
        console.log('[DiceMigration] disabled check:', {
          diceId,
          migratingDiceId,
          noDiceId,
          disabled,
          isMigrated,
          loading: isCurrentRowMigrating,
        });
      }

      return (
        <AppButton
          size="small"
          variant="soft"
          loading={isCurrentRowMigrating}
          disabled={disabled}
          onClick={() => onMigrate(record, diceId)}
          className="sourcing-shortlist-btn"
        >
          {isMigrated ? 'Migrated' : 'Migrate'}
        </AppButton>
      );
    },
  },
];

function joinName(candidate) {
  return [
    candidate.firstName,
    candidate.middleName,
    candidate.lastName,
  ].filter(Boolean).join(' ') || candidate.name || candidate.fullName || '-';
}

function normalizeSkills(candidate) {
  const rawSkills = candidate.skills ?? candidate.skill ?? candidate.primarySkills ?? candidate.technicalSkills ?? [];
  if (process.env.NODE_ENV === 'development') {
    console.log('Raw API Skills:', rawSkills);
  }

  let mappedSkills = [];
  if (Array.isArray(rawSkills)) {
    mappedSkills = rawSkills
      .map((skill) => (typeof skill === 'string' ? skill : skill?.skill ?? skill?.name ?? skill?.skillName ?? skill?.value))
      .filter(Boolean);
  } else if (typeof rawSkills === 'string') {
    mappedSkills = rawSkills.split(',').map((skill) => skill.trim()).filter(Boolean);
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('Mapped Skills:', mappedSkills);
  }

  return mappedSkills;
}

function normalizeCandidate(candidate, index) {
  const location = candidate.currentLocation ?? candidate.location ?? candidate.city ?? '-';
  const experience = candidate.totalExperience ?? candidate.experience ?? candidate.exp ?? candidate.yearsOfExperience;
  const createdOn = candidate.createdOn ?? candidate.createdAt ?? candidate.createdDate ?? candidate.updatedAt;
  const skills = normalizeSkills(candidate);
  const sourceFields = [
    candidate?.sourceType,
    candidate?.profileSource,
    candidate?.selectedSource,
    candidate?.customFields?.sourceType,
    candidate?.customFields?.profileSource,
  ].flat();
  const isDiceCandidate = sourceFields.some(
    (value) => String(value ?? '').trim().toLowerCase() === 'dice',
  ) || Boolean(candidate?.customFields?.diceProfileData);
  const diceProfileId = isDiceCandidate ? resolveDiceProfileId(candidate) : undefined;

  const mappedCandidate = {
    ...candidate,
    id: candidate.id ?? candidate._id ?? candidate.candidateId ?? `candidate-${index}`,
    ...(isDiceCandidate && {
      diceId: diceProfileId,
      candidateId: candidate.candidateId
        ?? candidate.customFields?.diceProfileData?.candidateId
        ?? '',
    }),
    name: joinName(candidate),
    designation: candidate.designation ?? candidate.currentDesignation ?? candidate.jobTitle ?? '-',
    location: location !== '-' ? location : '',
    exp: experience != null && experience !== '' ? `${experience} yrs` : '',
    locationExp: `${location} / ${experience || '-'} yrs`,
    skills,
    createdOn: createdOn ? String(createdOn) : '-',
    action: 'shortlist',
  };

  if (process.env.NODE_ENV === 'development') {
    console.log('Table Row Skills:', mappedCandidate.skills);
    if (isDiceCandidate) {
      console.log('[DiceSourcingRow] IDs after mapping:', {
        id: mappedCandidate.id,
        diceId: mappedCandidate.diceId,
        candidateId: mappedCandidate.candidateId,
      });
    }
  }

  return mappedCandidate;
}

function SectionTitle({ icon, title }) {
  return (
    <Space size={10} align="center">
      <div className="v1-section-icon-box">{icon}</div>
      <Text strong className="v1-section-label">{title}</Text>
    </Space>
  );
}

export default function SourcingTab({ jobId, onActionComplete, onDescriptionSaved }) {
  const [form] = Form.useForm();
  const [loading, setLoading]               = useState(false);
  const [results, setResults]               = useState([]);
  const [total, setTotal]                   = useState(0);
  const [currentPage, setCurrentPage]       = useState(1);
  const [lastSearchValues, setLastSearchValues] = useState(null);
  const [searched, setSearched]             = useState(false);
  const [collapsed, setCollapsed]           = useState(false);
  const [expRange, setExpRange]             = useState([0, 30]);
  const [selectedSources, setSelectedSources] = useState(['All', 'Internal']);
  const [viewMode, setViewMode] = useState('list');
  const [pageSize, setPageSize] = useState(10);
  const [migratingDiceId, setMigratingDiceId] = useState(null);
  const [migratedDiceIds, setMigratedDiceIds] = useState(() => new Set());
  const [searchScope, setSearchScope] = useState('internal');

  const handleDiceMigrate = async (candidate, diceId) => {
    console.log('Dice Profile ID:', diceId);
    console.log('Candidate Object:', candidate);
    setMigratingDiceId(diceId);
    try {
      const response = await migrateDiceCandidate(diceId);
      message.success(
        response?.message
        ?? response?.data?.message
        ?? 'Candidate migrated successfully.',
      );
      setMigratedDiceIds((current) => new Set(current).add(String(diceId)));
      onActionComplete?.(response);
    } catch (error) {
      message.error(error?.message || 'Failed to migrate candidate.');
    } finally {
      setMigratingDiceId(null);
    }
  };

  // Shortlist reuses the Tag to Jobs API directly — no modal — with the
  // "shortlisted" stage value resolved from the DB (workflow config), never
  // a hardcoded string.
  const handleShortlist = async ({ selectedIds }) => {
    if (!selectedIds.length) return;
    const hide = message.loading('Shortlisting candidate(s)...', 0);
    try {
      const shortlistedValue = await getShortlistedStageValue();
      if (!shortlistedValue) {
        throw new Error('Shortlisted stage is not configured');
      }
      await tagCandidatesToJob({
        jobId,
        candidateIds: selectedIds,
        hiringStage: shortlistedValue,
      });
      message.success('Candidate(s) shortlisted successfully.');
      onActionComplete?.();
    } catch (error) {
      message.error(error?.message || 'Failed to shortlist candidate(s).');
    } finally {
      hide();
    }
  };

  const sourcingActions = getSourcingActions({ onShortlist: handleShortlist });

  const candidateFields = getCandidateFields({
    onMigrate: handleDiceMigrate,
    migratingDiceId,
    migratedDiceIds,
  });

  // Force card view when Dice is selected as a source
  useEffect(() => {
    const hasDice = selectedSources.some(s => s.toLowerCase() === 'dice');
    if (hasDice && viewMode !== 'card') {
      setViewMode('card');
    }
  }, [selectedSources, viewMode]);
  const [jdDrawerOpen, setJdDrawerOpen] = useState(false);
  const [jdLoading, setJdLoading]       = useState(false);
  const [jdRaw, setJdRaw]               = useState('');
  const [jdEditing, setJdEditing]       = useState(false);
  const [jdEditRaw, setJdEditRaw]       = useState('');
  const [jdSaving, setJdSaving]         = useState(false);

  // Clear cached description whenever the job changes
  useEffect(() => { setJdRaw(''); }, [jobId]);

  const DESCRIPTION_KEYS = ['jobDescription', 'description', 'rawDescription', 'jobDescriptionText'];

  const handleOpenJD = async () => {
    setJdDrawerOpen(true);
    if (jdRaw) return;
    setJdLoading(true);
    try {
      const formGroups = await getFormGroups({ module: 'job', id: jobId });
      const groups = Array.isArray(formGroups) ? formGroups : [];
      let raw = '';
      for (const group of groups) {
        for (const field of group.fields ?? []) {
          const matchesKey   = DESCRIPTION_KEYS.includes(field.field);
          const matchesLabel = /description/i.test(field.label ?? '');
          if ((matchesKey || matchesLabel) && typeof field.value === 'string' && field.value.trim()) {
            raw = field.value;
            break;
          }
        }
        if (raw) break;
      }
      setJdRaw(raw);
    } finally {
      setJdLoading(false);
    }
  };

  const handleEditStart = () => {
    setJdEditRaw(jdRaw);
    setJdEditing(true);
  };

  const handleEditCancel = () => {
    setJdEditing(false);
    setJdEditRaw('');
  };

  const handleEditSave = async () => {
    if (!jdEditRaw.trim()) {
      message.warning('Please enter a job description');
      return;
    }
    setJdSaving(true);
    try {
      await updateJobDescription(jobId, jdEditRaw.trim());
      setJdRaw('');
      setJdEditing(false);
      setJdEditRaw('');
      setJdLoading(true);
      const formGroups = await getFormGroups({ module: 'job', id: jobId });
      const groups = Array.isArray(formGroups) ? formGroups : [];
      let raw = '';
      for (const group of groups) {
        for (const field of group.fields ?? []) {
          const matchesKey   = DESCRIPTION_KEYS.includes(field.field);
          const matchesLabel = /description/i.test(field.label ?? '');
          if ((matchesKey || matchesLabel) && typeof field.value === 'string' && field.value.trim()) {
            raw = field.value;
            break;
          }
        }
        if (raw) break;
      }
      setJdRaw(raw);
      message.success('Description updated and parsed successfully');
      onDescriptionSaved?.();
    } catch {
      message.error('Failed to update description');
    } finally {
      setJdSaving(false);
      setJdLoading(false);
    }
  };

  const resolvedSources = () => {
    const active = selectedSources.filter(s => s !== 'All');
    return active.length ? active.map(s => s.toLowerCase()) : ['internal'];
  };

  const buildPayload = (values, offset = 0, size = pageSize) => {
    const srcs = resolvedSources();
    const scope = srcs.includes('dice') ? 'dice' : 'internal';
    return {
      tenantId:        localStorage.getItem('tenantId') || '',
      businessId:      localStorage.getItem('businessId') || '',
      businessUnitId:  localStorage.getItem('businessUnitId') || '',
      jobId,
      filter: {
        skills:         values.skills   ? [values.skills]   : [],
        location:       values.location ? [values.location] : [],
        jobTitle:       values.jobTitle ? [values.jobTitle] : [],
        experienceFrom: String(expRange[0]),
        experienceTo:   expRange[1] >= 30 ? '' : String(expRange[1]),
      },
      hiringStage:     '',
      hiringStageType: '',
      sortBy:          'relevance',
      sortType:        'asc',
      booleanQuery:    values.booleanQuery || '',
      searchFilter:    {},
      diceFilter:      {},
      searchQuery:     '',
      postedDate:      values.createdDate || '',
      sourceType:      srcs.includes('internal') ? 'internal' : srcs[0] || 'internal',
      selectedSource:  srcs,
      scope:           scope,
      userId:          Number(localStorage.getItem('userId')) || 0,
      offset,
      limit: size,
    };
  };

  const runSearch = async (values, page = 1, size = pageSize) => {
    const offset = (page - 1) * size;
    setLoading(true);
    try {
      const res   = await searchCandidates(buildPayload(values, offset, size), offset, size);
      const list  = res?.data?.data ?? res?.data?.applicant ?? [];
      const count = res?.data?.count?.searchCount ?? res?.data?.count?.total ?? res?.count?.searchCount ?? res?.count?.total ?? 0;
      
      // Check if API response indicates Dice data source
      const hasDiceInResponse = list.some(record => {
        const sourceType = record?.sourceType ?? record?.profileSource ?? '';
        return String(sourceType).toLowerCase() === 'dice';
      });
      
      // Force card view if Dice is in the response, even if not explicitly selected
      if (hasDiceInResponse && viewMode !== 'card') {
        setViewMode('card');
      }
      
      setResults(list.map(normalizeCandidate));
      setTotal(count);
      setCurrentPage(page);
      setLastSearchValues(values);
      setSearched(true);
      setCollapsed(true);
    } catch (error) {
      console.error('Candidate search failed:', error);
      message.error('Failed to search candidates. Please try again.');
      setResults([]);
      setTotal(0);
      setSearched(true);
    }finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => runSearch(values, 1);

  const handlePageChange = (page, newSize) => {
    if (!lastSearchValues) return;
    if (newSize && newSize !==pageSize) setPageSize(newSize);
    runSearch(lastSearchValues, page, newSize ?? pageSize);
  };

  const handleReset = () => {
    form.resetFields();
    setResults([]);
    setTotal(0);
    setCurrentPage(1);
    setLastSearchValues(null);
    setSearched(false);
    setCollapsed(false);
    setExpRange([0, 30]);
    setSelectedSources(['All', 'Internal']);
  };

  const toggleSource = (label, checked) => {
    if (label === 'All') {
      setSelectedSources(checked ? SOURCE_OPTS : []);
      return;
    }
    setSelectedSources(prev => {
      const next        = checked ? [...prev.filter(s => s !== 'All'), label] : prev.filter(s => s !== label);
      const allNonAll   = SOURCE_OPTS.filter(s => s !== 'All');
      const allChecked  = allNonAll.every(s => next.includes(s));
      return allChecked ? ['All', ...next] : next.filter(s => s !== 'All');
    });
  };

  return (
    <div className="sourcing-tab">
      <div className="sourcing-v1-scope v1-edit-page">
        <div className="v1-page-inner">
          <Card
            className="v1-card v1-edit-card"
            title={
              <SectionTitle
                icon={<FilterOutlined />}
                title="Build Your Search Query"
              />
            }
            extra={
              <Space size={8}>
                <AppButton size="small" onClick={handleReset}>
                  Reset filters
                </AppButton>

                <AppButton
                  variant="primary"
                  size="small"
                  icon={<SearchOutlined />}
                  loading={loading}
                  onClick={() => form.submit()}
                >
                  Search
                </AppButton>

                <AppButton size="small" variant="soft" icon={<RobotOutlined />}>
                  AI Search
                </AppButton>
                
                <AppButton size="small" variant="soft" icon={<FileTextOutlined />} onClick={handleOpenJD}>
                  Job Description
                </AppButton>

                <AppButton
                  type="text"
                  size="small"
                  icon={collapsed ? <DownOutlined /> : <UpOutlined />}
                  onClick={() => setCollapsed(c => !c)}
                />
              </Space>
            }
          >
            {!collapsed && (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSearch}
                className="v1-form"
                colon={false}
              >
                <Row gutter={[24, 0]}>
                  <Col xs={24}>
                    <Form.Item name="booleanQuery" label="Keyword / Boolean Query">
                      <Input
                        placeholder='Enter keyword, e.g., Java OR (J2EE AND "Apache Kafka")'
                        prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="createdDate" label="Created Date">
                      <Select
                        placeholder="Select created date"
                        allowClear
                        options={DATE_OPTIONS}
                        suffixIcon={<DownOutlined />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item name="location" label="Location">
                      <Input placeholder="Enter location" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[24, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="jobTitle" label="Job Title">
                      <Input placeholder="Enter job title" />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item name="skills" label="Skills">
                      <Input placeholder="Enter skills" />
                    </Form.Item>
                  </Col>
                </Row>

                <div className="sourcing-exp-section">
                  <Text className="sourcing-exp-title">Experience in Years</Text>

                  <Slider
                    range
                    min={0}
                    max={30}
                    marks={EXP_MARKS}
                    value={expRange}
                    onChange={setExpRange}
                    tooltip={{ formatter: v => (v >= 30 ? '30+' : String(v)) }}
                    className="sourcing-exp-slider"
                  />

                  <Space size={8} className="sourcing-exp-inputs" wrap={false}>
                    <Text className="sourcing-exp-input-label">From</Text>

                    <Input
                      size="small"
                      className="sourcing-exp-num-input"
                      value={expRange[0]}
                      onChange={e => {
                        const v = parseInt(e.target.value, 10) || 0;
                        setExpRange([Math.min(v, expRange[1]), expRange[1]]);
                      }}
                    />

                    <Text className="sourcing-exp-input-label">To</Text>

                    <Input
                      size="small"
                      className="sourcing-exp-num-input"
                      value={expRange[1] >= 30 ? '30+' : expRange[1]}
                      onChange={e => {
                        const v = parseInt(e.target.value, 10) || 0;
                        setExpRange([expRange[0], Math.min(v, 30)]);
                      }}
                    />

                    <Text className="sourcing-exp-input-label">Years</Text>
                  </Space>
                </div>

                <div className="sourcing-pref-section">
                  <Text className="sourcing-pref-label">Search Preference</Text>
                  <span className="sourcing-pref-sep">—</span>

                  <Space size={12} wrap>
                    {SOURCE_OPTS.map(label => (
                      <Checkbox
                        key={label}
                        checked={selectedSources.includes(label)}
                        onChange={e => toggleSource(label, e.target.checked)}
                      >
                        {label}
                      </Checkbox>
                    ))}
                  </Space>
                </div>
              </Form>
            )}
          </Card>
        </div>
      </div>

      <Spin spinning={loading}>
        {!searched && !loading && (
          <Empty
            style={{ marginTop: 24 }}
            description="Use the search above to find candidates"
          />
        )}

        {searched && results.length === 0 && !loading && (
          <Empty
            style={{ marginTop: 24 }}
            description="No candidates found for this query"
          />
        )}

        {searched && results.length > 0 && viewMode === 'list' && (
          <ParamListView
            listName="Internal"
            moduleName="candidate"
            actions={sourcingActions}
            fields={candidateFields}
            dataSource={results}
            loading={loading}
            total={total}
            current={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={(newSize) => handlePageChange(1, newSize)}
            className="sourcing-results-list"
            activeView={viewMode}
            onViewChange={setViewMode}
          />
        )}

        {searched && results.length > 0 && viewMode === 'card' && (
          <ParamCardView
            listName="Internal"
            moduleName="candidate"
            actions={sourcingActions}
            fields={candidateFields}
            dataSource={results}
            loading={loading}
            total={total}
            current={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={(newSize) => handlePageChange(1, newSize)}
            className="sourcing-results-list"
            activeView={viewMode}
            onViewChange={setViewMode}
          />
        )}

        {false && results.map((c, i) => (
          <div key={c._id || i} className="candidate-result-row">
            <strong>{c.firstName} {c.lastName}</strong>
            {c.designation && <span> — {c.designation}</span>}
            {c.currentLocation && <span> — {c.currentLocation}</span>}
          </div>
        ))}

        {false && searched && total > 0 && (
          <div className="sourcing-total">Total: {total} candidates found</div>
        )}
      </Spin>

      <Drawer
        title={<SectionTitle icon={<FileTextOutlined />} title="Job Description" />}
        placement="right"
        width={Math.min(860, window.innerWidth * 0.85)}
        open={jdDrawerOpen}
        onClose={() => { setJdDrawerOpen(false); setJdEditing(false); setJdEditRaw(''); }}
        destroyOnHidden
        rootClassName="v1-edit-page"
        styles={{
          header: {
            background: 'linear-gradient(120deg, rgba(0, 83, 165, 0.07) 0%, #ffffff 65%)',
            borderBottom: '1px solid #d6e4f0',
            minHeight: 56,
            padding: '0 20px',
            alignItems: 'center',
          },
          body: { padding: '20px 24px', background: '#f7fafd' },
        }}
        extra={
          !jdLoading && !jdEditing && (
            <AppButton
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={handleEditStart}
            >
              Edit
            </AppButton>
          )
        }
      >
        <Spin spinning={jdLoading}>
          {jdEditing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                background: '#ffffff',
                border: '1px solid #d6e4f0',
                borderRadius: 8,
                padding: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                <Input.TextArea
                  rows={14}
                  value={jdEditRaw}
                  onChange={(e) => setJdEditRaw(e.target.value)}
                  placeholder="Paste raw job description here..."
                  style={{
                    fontSize: 13,
                    lineHeight: 1.65,
                    border: 'none',
                    boxShadow: 'none',
                    resize: 'vertical',
                    padding: '12px 14px',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <AppButton
                  variant="primary"
                  size="small"
                  loading={jdSaving}
                  onClick={handleEditSave}
                >
                  Save & Parse
                </AppButton>
                <AppButton size="small" onClick={handleEditCancel}>Cancel</AppButton>
              </div>
            </div>
          ) : (
            <div style={{
              background: '#ffffff',
              border: '1px solid #d6e4f0',
              borderRadius: 8,
              padding: '18px 20px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}>
              <DescriptionBlock raw={jdRaw} />
            </div>
          )}
        </Spin>
      </Drawer>
    </div>
  );
}
